import { Accordion, Button, LoadingOverlay } from '@mantine/core';
import { useForm } from '@mantine/form';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import type { Environment } from '@prisma/client';
import { useProjectId } from '~/hooks/context/useProjectId';
import type { TestSuiteFetchResult } from '~/server/api/routers/test-suite-router';
import { AUTO_CONCURRENCY } from '~/server/logic/common/workers';
import { api } from '~/trpc-extra/api';
import { SlackHookUpdater } from '../auth/SlackHookUpdater';
import { EnvironmentSection } from './EnvironmentSection';
import { ExecutionOptionsSection } from './ExecutionOptionsSection';
import { NotificationsSection } from './NotificationsSection';
import { ParallelismSection } from './ParallelismSection';
import { SetupTeardownSection } from './SetupTeardownSection';
import { getCommonSettingsFromSuite } from './form';
import { parseVariableValue } from '~/common/variable/value';

export type WorkerMode = 'manual' | 'auto';

export default function SuiteSettings({
  testSuite,
  onClose,
}: {
  testSuite: TestSuiteFetchResult;
  onClose: () => void;
}) {
  const trpcUtils = api.useUtils();
  const projectId = useProjectId();

  const { mutate: updateSuite, isPending: isSaving } = api.testSuite.update.useMutation({
    onSuccess: async () => {
      await trpcUtils.testSuite.get.invalidate().catch(() => undefined);
      notifications.show({ message: 'Successfully updated', color: 'blue' });
      modals.closeAll();
      onClose();
    },
    onError: (error) => {
      notifications.show({ message: `Error saving: ${error.message}`, color: 'red' });
    },
  });

  const { data: organizationEmailAddress, isLoading: isLoadingOrganizationEmailAddress } =
    api.stablyEmail.getOrganizationEmailAddress.useQuery({
      projectId: projectId!,
    });
  const orgPrefix = organizationEmailAddress?.split('@').at(0)?.trim() || '';

  const form = useForm({
    initialValues: {
      ...getCommonSettingsFromSuite(testSuite),
      setupTestId: testSuite.setupTestId ?? '',
      teardownTestId: testSuite.teardownTestId ?? '',
      beforeEachTestId: testSuite.beforeEachTestId ?? '',
      afterEachTestId: testSuite.afterEachTestId ?? '',
    },
  });

  return (
    projectId && (
      <form
        className="flex flex-col gap-3"
        onSubmit={form.onSubmit(
          async ({
            maxRetries,
            operationDelayMs,
            testTimeoutSeconds,
            numberOfWorkers,
            workerMode,
            notificationProfile,
            setupTestId,
            teardownTestId,
            beforeEachTestId,
            afterEachTestId,
            environment,
            variableOverrides,
          }) => {
            const variableOverridesMap = Object.fromEntries(
              variableOverrides.map(({ name, value, sensitive }) => [
                name,
                { value: parseVariableValue(value), sensitive },
              ]),
            );
            updateSuite({
              id: testSuite.id,
              projectId,
              data: {
                maxRetries,
                operationDelayMs,
                testTimeoutMs: testTimeoutSeconds * 1000,
                numberOfWorkers: workerMode === 'manual' ? numberOfWorkers : AUTO_CONCURRENCY,
                notificationProfile,
                setupTestId,
                teardownTestId,
                beforeEachTestId,
                afterEachTestId,
                environment: environment ?? ('NONE' satisfies Environment),
                variableOverrides: variableOverridesMap,
              },
            });
          },
        )}
      >
        <SlackHookUpdater
          projectId={projectId || ''}
          testSuiteId={testSuite.id}
        />
        These settings will be used for this test suite
        <div className="relative">
          <LoadingOverlay
            visible={isSaving}
            zIndex={1000}
            overlayProps={{ radius: 'sm', blur: 2 }}
          />
          <Accordion
            variant="separated"
            multiple
            defaultValue={['execution', 'parallelism']}
          >
            <ExecutionOptionsSection
              allowSelectRetryBehavior={false}
              form={form}
              testSuiteIsLoading={isSaving}
            />

            <ParallelismSection form={form} />

            <SetupTeardownSection form={form} />

            <EnvironmentSection
              form={form}
              orgPrefix={orgPrefix}
              organizationEmailAddress={organizationEmailAddress}
              isLoadingOrganizationEmailAddress={isLoadingOrganizationEmailAddress}
              variableOverrides={form.values.variableOverrides}
            />

            <NotificationsSection
              form={form}
              projectId={projectId}
              testSuiteId={testSuite.id}
              notificationProfile={testSuite.notificationProfile ?? 'PROJECT'}
              hideChannelConfiguration={false}
            />
          </Accordion>
        </div>
        <div className="flex flex-row justify-end">
          <Button type="submit">Save</Button>
        </div>
      </form>
    )
  );
}
